import { createContext, ReactNode, useEffect, useMemo, useState } from 'react'
import { getAuth, User, onAuthStateChanged } from 'firebase/auth'
import { initializeApp } from 'firebase/app'
import { PermissionEnum } from 'api/userPermissions'
import { Loading } from 'ui'
import { getWhoami } from 'api/company'
import { isDemoMode, reactivateDemoMode } from 'api/demo'
import { config } from 'config'
import { useSyncTelemetryUser } from '@workwhile/telemetry/react'
import { useAuthContext } from './useAuthContext'
import {
  setAmplitudeUserId,
  useAmplitudeIdentify,
  reset,
  identify,
} from 'lib/amplitude'
import { IIdentify } from '@amplitude/analytics-browser/lib/esm/types'
import { useWhoami } from 'queries/company'
import { QueryFnData } from 'queries/types'
import { STORED_COMPANY_ID_KEY } from '../../constants'
import { AuthCompany, AuthCompanyListItem, UserPermissions } from './types'

const TelemetryUserSync = () => {
  const { user, company } = useAuthContext()

  const telemetryUser = useMemo(() => {
    return {
      id: user?.uid,
      email: user?.email || undefined,
      companyId: company?.id,
    }
  }, [user, company])

  useSyncTelemetryUser(telemetryUser)

  return null
}

const app = initializeApp(config.firebaseConfig)
export const auth = getAuth(app)

export interface AuthContextValueProps {
  user: User | null
  company: AuthCompany | null
  isAuthenticated: boolean
  companies: AuthCompanyListItem[]
  permissions: UserPermissions | null
  hasPermission: (permission: PermissionEnum) => boolean
  identity: IIdentify
  companyChangedInAnotherTab: boolean
}

export const AuthContext = createContext<AuthContextValueProps>(null!)

interface Props {
  children: ReactNode
}

type WhoamiCompany = QueryFnData<typeof getWhoami>['companies'][number]

function toAuthCompany(
  company: WhoamiCompany,
  allCompanies: WhoamiCompany[]
): AuthCompany {
  return {
    id: company.companyId,
    name: company.companyName,
    logoUrl: company.logoUrl,
    takePercentageEstimate: Number(company.takePercentageEstimate),
    bonusTakePercentageEstimate: Number(company.bonusTakePercentageEstimate),
    chargeUpfront: company.chargeUpfront,
    w2: company.w2,
    backfillPolicy: company.backfillPolicy,
    hasVNDLYIntegration: company.hasVNDLYIntegration,
    isInternalWorkforceEnabled: company.isInternalWorkforceEnabled,
    companyGroup: company.companyGroup
      ? {
          id: company.companyGroup.id,
          companies: allCompanies
            .filter(
              (item) => item.companyGroup?.id === company.companyGroup?.id
            )
            .map((item) => ({
              id: item.companyId,
              name: item.companyName,
            })),
        }
      : null,
  }
}

export const AuthProvider = (props: Props) => {
  const [initializing, setInitializing] = useState(true)
  const [user, setUser] = useState<User | null>(null)
  const [companyChangedInAnotherTab, setCompanyChangedInAnotherTab] =
    useState(false)
  const identity = useAmplitudeIdentify()

  const { data: whoami, isFetching: isLoadingWhoami } = useWhoami(user?.uid)

  const companies = useMemo(
    () =>
      whoami?.companies.map((company) => ({
        id: company.companyId,
        name: company.companyName,
      })) ?? [],
    [whoami]
  )

  const company = useMemo(() => {
    const currentCompany = whoami?.companies.find((item) => item.isDefault)
    if (!currentCompany || !whoami) {
      return null
    }
    return toAuthCompany(currentCompany, whoami.companies)
  }, [whoami])

  const permissions: UserPermissions | null = whoami?.permissions[0] ?? null

  useEffect(() => {
    return onAuthStateChanged(auth, async (user) => {
      setUser(user)
      if (user?.uid) {
        setAmplitudeUserId(user.uid)
      } else {
        reset()
      }
      if (initializing) {
        setInitializing(false)
      }
    })
  }, [])

  useEffect(() => {
    const isDemo = isDemoMode()
    if (isDemo) {
      reactivateDemoMode()
    }
  }, [])

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key !== STORED_COMPANY_ID_KEY) {
        return
      }

      const newCompanyId = event.newValue
      if (newCompanyId && newCompanyId !== company?.id?.toString()) {
        setCompanyChangedInAnotherTab(true)
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [company])

  useEffect(() => {
    if (company?.id) {
      localStorage.setItem(STORED_COMPANY_ID_KEY, company.id.toString())
    }
  }, [company])

  useEffect(() => {
    if (!user) {
      return
    }

    if (company) {
      identity.set('company_id', company.id)
    } else {
      identity.unset('company_id')
    }

    setAmplitudeUserId(user.uid)
    identity.set('user_id', user.uid)
    identify(identity)
  }, [user, company, identity])

  const isLoadingAuthData = !!user && isLoadingWhoami

  const values = useMemo(() => {
    if (initializing || isLoadingAuthData) {
      return null
    }

    return {
      user,
      company,
      companies,
      permissions,
      isAuthenticated: !!user,
      hasPermission: (permission: PermissionEnum) => {
        if (!permissions) {
          return false
        }
        return permissions[permission]
      },
      identity,
      companyChangedInAnotherTab,
    } satisfies AuthContextValueProps
  }, [
    user,
    permissions,
    company,
    companies,
    identity,
    initializing,
    isLoadingAuthData,
    companyChangedInAnotherTab,
  ])

  if (initializing || isLoadingAuthData || !values) {
    return <Loading />
  }

  return (
    <AuthContext.Provider value={values}>
      <TelemetryUserSync />
      {props.children}
    </AuthContext.Provider>
  )
}
